import React, { useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ClipLoader } from "react-spinners";
import { FEATURE_ID, GENERAL, KEY, ROUTE } from '../../Common/constant';
import { getAllArticles, getAllNews } from '../../Common/Service/api';
import FooterOne from '../Footer/FooterOne';
import Header from '../Header/HeaderOne';
import { pageTitle } from '../PageTitle';
import { allowedWebsiteTypes } from '../../Common/data';


const NewsArticles = () => {
    pageTitle('News & Articles');

    const navigate = useNavigate()
    const { state } = useLocation()

    const newsRef = useRef(null)
    const articleRef = useRef(null)

    const onNavigateToDetail = (type, data) => () => {
        const params = { type, data }
        if (type === FEATURE_ID.NEWS) navigate(ROUTE.NEWS.DETAIL.replace(":slug", data.slug), { state: params })
        if (type === FEATURE_ID.ARTICLE) navigate(ROUTE.ARTICLES.DETAIL.replace(":slug", data.slug), { state: params })
    }

    const onFormatNewsContent = (contents) => {
        const contentParagraphArray = []

        for (const content of contents) {
            const text = content.children[0].text || "\n\n"
            contentParagraphArray.push(text)
        }

        if (contentParagraphArray.length > 0) return contentParagraphArray.join("")
        else return ""
    }

    const { data: newsResponse, isLoading: loadingNews } = useQuery({
        queryKey: [KEY.QUERY.ALL_NEWS],
        queryFn: getAllNews,
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60
    })

    const { data: articlesResponse, isLoading: loadingArticles } = useQuery({
        queryKey: [KEY.QUERY.ALL_ARTICLES],
        queryFn: getAllArticles,
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60
    })

    const allNews = useMemo(() => {
        const store = []

        if (newsResponse) {
            const responseData = newsResponse?.data
            if (responseData.data.length > 0) {
                let count = 0
                for (const itemNews of responseData.data) {
                    const attributes = itemNews.attributes
                    const imageUrl = attributes.cover.data.attributes.url
                    const website = attributes.website
                    const isShownInEGP = allowedWebsiteTypes.includes(String(website).toLowerCase())
                    const obj = {
                        id: itemNews.id,
                        title: attributes.title,
                        date: dayjs(attributes.published_date).format("DD MMMM YYYY"),
                        author: attributes.source || "Anonymous",
                        image: imageUrl ? `${GENERAL.BASE_URL}${imageUrl}` : null,
                        slug: attributes.slug,
                        highlight: attributes.highlight,
                        content: onFormatNewsContent(attributes.content),
                        rawContent: attributes.content,
                        category: attributes.category || null,
                        tags: attributes.tags || [],
                        data_aos_delay: `${100 * count}ms`,
                    }
                    if (isShownInEGP) {
                        count++
                        store.push(obj)
                    }
                }
            }
        }
        return store
    }, [newsResponse])

    const allArticles = useMemo(() => {
        const store = []

        if (articlesResponse) {
            const responseData = articlesResponse?.data
            if (responseData.data.length > 0) {
                let count = 0
                for (const itemArticle of responseData.data) {
                    const attributes = itemArticle.attributes
                    const imageUrl = attributes.cover.data.attributes.url
                    const website = attributes.website
                    const isShownInEGP = allowedWebsiteTypes.includes(String(website).toLowerCase())
                    const obj = {
                        id: itemArticle.id,
                        title: attributes.title,
                        date: dayjs(attributes.published_date).format("DD MMMM YYYY"),
                        author: attributes.author || "Anonymous",
                        image: imageUrl ? `${GENERAL.BASE_URL}${imageUrl}` : null,
                        slug: attributes.slug,
                        highlight: attributes.highlight,
                        content: attributes.content,
                        category: attributes.category,
                        tags: attributes.tags,
                        data_aos_delay: `${100 * count}ms`,
                    }
                    if (isShownInEGP) {
                        count++
                        store.push(obj)
                    }
                }
            }
        }

        return store
    }, [articlesResponse])

    useEffect(() => {
        if (!state) window.scrollTo(0, 0)
        if (state === FEATURE_ID.NEWS) window.scrollTo(0, newsRef.current.offsetTop)
        if (state === FEATURE_ID.ARTICLE) window.scrollTo(0, articleRef.current.offsetTop)
    }, [state])

    return (
        <main ref={newsRef}>
            <Header />
            <div className="blog__page p_relative see__pad">
                <div className="auto-container">
                    <div className="sub__title">
                        <h4 style={{ display: "flex", alignItems: "center", gap: "12px" }}>NEWS<ClipLoader color="#ff8c2e" size={24} loading={loadingNews} /> </h4>
                    </div>
                    <div style={{ marginTop: "32px" }}></div>
                    <div className="row">
                        {allNews.map((item) => (
                            <div className="col-lg-4 col-md-6 col-sm-12 news__block" key={item.id}>
                                <div className="news___block wow slideInUp animated animated" data-wow-delay={item.data_aos_delay} data-wow-duration="1500ms">
                                    <button onClick={onNavigateToDetail(FEATURE_ID.NEWS, item)}>
                                        <div className="inner-box p_relative">
                                            <div className="image-box p_relative d_block">
                                                <figure className="image p_relative d_block">
                                                    {item.image ?
                                                        <img src={item.image} alt="" loading='lazy' />
                                                        :
                                                        <div style={{ display: "flex", width: "100%", height: "400px", justifyContent: "center", alignItems: "center", background: "#BCBCBC", paddingBottom: "75px" }}>
                                                            <svg
                                                                fill="#666"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="40px"
                                                                height="40px"
                                                                viewBox="0 0 52 52"
                                                                enableBackground="new 0 0 52 52"
                                                                xmlSpace="preserve"
                                                            >
                                                                <path d="M50,10c0-2.2-1.8-4-4-4H6c-2.2,0-4,1.8-4,4v32c0,2.2,1.8,4,4,4h40c2.2,0,4-1.8,4-4V10z M39.6,38h-29 c-1.2,0-1.9-1.3-1.3-2.3l8.8-15.3c0.4-0.7,1.3-0.7,1.7,0l5.3,9.1c0.4,0.6,1.3,0.7,1.7,0.1l4.3-6.2c0.4-0.6,1.3-0.6,1.7,0L40.7,36 C41.3,36.9,40.7,38,39.6,38z M37,20c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S39.2,20,37,20z" />
                                                            </svg>
                                                        </div>
                                                    }
                                                </figure>
                                                <div className="post__date">
                                                    <ul>
                                                        <li> <i className="icon-15"></i>{item.date}</li>
                                                        <li className="two"></li>
                                                        <li><i className="icon-09"></i> {item.author}</li>
                                                    </ul>
                                                </div>
                                            </div>
                                            <div className="lower___content p_relative">
                                                <h4>
                                                    {item.title}
                                                </h4>
                                                <p>{item.highlight}</p>

                                                <div className="btn__box__two">
                                                    <div className="theme__btn__two">
                                                        <i className="icon-02"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ marginTop: "64px" }}></div>

                <div ref={articleRef} className="auto-container">
                    <div className="sub__title">
                        <h4 style={{ display: "flex", alignItems: "center", gap: "12px" }}>ARTICLES<ClipLoader color="#ff8c2e" size={24} loading={loadingArticles} /> </h4>
                    </div>
                    <div style={{ marginTop: "32px" }}></div>
                    <div className="row">
                        {allArticles.map((item) => (
                            <div className="col-lg-4 col-md-6 col-sm-12 news__block" key={item.id}>
                                <div className="news___block wow slideInUp animated animated" data-wow-delay={item.data_aos_delay} data-wow-duration="1500ms">
                                    <button onClick={onNavigateToDetail(FEATURE_ID.ARTICLE, item)}>
                                        <div className="inner-box p_relative">
                                            <div className="image-box p_relative d_block">
                                                <figure className="image p_relative d_block">
                                                    {item.image ?
                                                        <img src={item.image} alt="" loading='lazy' />
                                                        :
                                                        <div style={{ display: "flex", width: "100%", height: "400px", justifyContent: "center", alignItems: "center", background: "#BCBCBC", paddingBottom: "75px" }}>
                                                            <svg
                                                                fill="#666"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="40px"
                                                                height="40px"
                                                                viewBox="0 0 52 52"
                                                                enableBackground="new 0 0 52 52"
                                                                xmlSpace="preserve"
                                                            >
                                                                <path d="M50,10c0-2.2-1.8-4-4-4H6c-2.2,0-4,1.8-4,4v32c0,2.2,1.8,4,4,4h40c2.2,0,4-1.8,4-4V10z M39.6,38h-29 c-1.2,0-1.9-1.3-1.3-2.3l8.8-15.3c0.4-0.7,1.3-0.7,1.7,0l5.3,9.1c0.4,0.6,1.3,0.7,1.7,0.1l4.3-6.2c0.4-0.6,1.3-0.6,1.7,0L40.7,36 C41.3,36.9,40.7,38,39.6,38z M37,20c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S39.2,20,37,20z" />
                                                            </svg>
                                                        </div>
                                                    }
                                                </figure>
                                                <div className="post__date">
                                                    <ul>
                                                        <li> <i className="icon-15"></i>{item.date}</li>
                                                        <li className="two"></li>
                                                        <li><i className="icon-09"></i>{item.author}</li>
                                                    </ul>
                                                </div>
                                            </div>
                                            <div className="lower___content p_relative">
                                                <h4>
                                                    {item.title}
                                                </h4>
                                                <p>{item.highlight}</p>

                                                <div className="btn__box__two">
                                                    <div className="theme__btn__two">
                                                        <i className="icon-02"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: "32px" }} />

                    <div className='editorial-wrapper'>
                        <h4>Have an interesting article?</h4>
                        <p>
                            Every article submitted will be featured on our article page. Join us in sharing your unique insights and stories with our readers!
                        </p>
                        <Link to="mailto:info@k-egp.com" className="theme-btn theme-btn-one">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={24} height={24}>
                                <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z" />
                                <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z" />
                            </svg>
                            Send your article
                        </Link>
                    </div>
                </div>

                <div style={{ marginTop: "64px" }}></div>
            </div >
            <FooterOne />
        </main>
    );
};

export default NewsArticles;
